import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertComponent, BodyanalyticsBaseComponent, RouterUrlsEnum } from '@bodyanalytics/ui-core';

import { FormControl, FormGroup, Validators } from '@angular/forms';
import { RegisterService } from '../../services/register.service';
import { RegistrationI } from '@bodyanalytics/data-models-ui';

import { AuthService } from '@auth0/auth0-angular';
import { RegisterAccountFormComponent } from '../../components/account-form/account-form.component';

/**
 * Account-creation onboarding step (#1760 chain). `createUser` provisions the
 * Auth0 user + CUSTOMER role; the app's Customer row does NOT exist yet. The
 * post-signup `loginWithRedirect` targets the member dashboard, whose identity
 * guard probes the profile endpoint, gets the tagged CUSTOMER_NOT_FOUND 404,
 * and lands the user on /register/profile — the step that creates the row.
 */
@Component({
  selector: 'bodyanalytics-register-init',
  imports: [AlertComponent, RegisterAccountFormComponent],
  templateUrl: './register-init.component.html',
  styleUrls: ['./register-init.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterInitComponent extends BodyanalyticsBaseComponent {
  private registerService = inject(RegisterService);
  auth = inject(AuthService);

  /** User-facing signup error — the old silent failure was half of the #1760 dead-end. */
  readonly errorMessage = signal('');

  public signupDetailsForm = new FormGroup({
    emailAddress: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
  });

  update(registration: RegistrationI): void {
    this.errorMessage.set('');
    this.subscriptions.barrel = this.registerService.createUser(registration).subscribe({
      next: response => {
        if (response === 204) {
          // Auth0 account created. Log in aiming at the dashboard: its identity
          // guard sees there is no Customer row yet (tagged 404) and forwards to
          // /register/profile, where the row is actually created (#1760).
          this.auth.loginWithRedirect({ appState: { target: RouterUrlsEnum.AUTH_DASHBOARD } });
        } else {
          this.errorMessage.set('We could not create your account. Please try again.');
        }
      },
      error: (err: unknown) => {
        // Auth0 tags a duplicate signup as 400 {"code":"invalid_signup"} — the
        // exact failure that used to loop silently (#1760). Point those users at
        // login; keep other failures generic (e.g. password policy).
        const code = err instanceof HttpErrorResponse ? (err.error as { code?: unknown } | null)?.code : undefined;
        this.errorMessage.set(
          code === 'invalid_signup'
            ? 'An account already exists for this email. Log in instead to continue.'
            : 'We could not create your account. Please check your details and try again.',
        );
      },
    });
  }
}
